import { useEffect, useLayoutEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { db, auth, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { MembershipContext } from '../contexts/MembershipContext';
import { useContact } from '../contexts/ContactContext';
import { getInitiatorPedalIds, getUserReceivesCash, getNetCashReceived, userNeedsPayoutOnboarding } from '../utils/tradeUtils';
import { openCheckout } from '../services/nativeBrowser';
import PayoutAccountLinkedModal from '../components/PayoutAccountLinkedModal';

const STRIPE_FN_BASE = 'https://us-central1-pedalxchange.cloudfunctions.net';

const TradeSettlement = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getTradeCompletionFee, getTradeProtectFee } = useContext(MembershipContext);
  const { openContact } = useContact();
  const [trade, setTrade] = useState(null);
  // { reconnected } — informational alreadyOnboarded notice (no navigation).
  const [payoutLinkedNotice, setPayoutLinkedNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  // Auth resolution + confirmed-missing flags so we never flash "Trade not found"
  // to a logged-in user during load / pull-to-refresh (which does a full reload).
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [authChecked, setAuthChecked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [includeProtection, setIncludeProtection] = useState(false);
  const [usernames, setUsernames] = useState({ fromUser: '', toUser: '' });
  // null = still determining; true/false = Stripe payout onboarding complete or not.
  // Gates settling for cash-RECEIVERS so they can't proceed without a usable
  // payout account (e.g. after dismissing the onboarding sheet without finishing).
  const [payoutReady, setPayoutReady] = useState(null);
  const [settingUpPayout, setSettingUpPayout] = useState(false);
  // Referral credit on this user's account — waives the platform fee so the fees
  // shown here match what TradeConfirm displayed (no confirm-vs-settlement drift).
  const [referralCredits, setReferralCredits] = useState(0);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading, id]);

  useEffect(() => {
    const fetchTrade = async () => {
      try {
        const docRef = doc(db, 'tradeOffers', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const tradeData = { id: snap.id, ...snap.data() };
          setNotFound(false);
          setTrade(tradeData);
          
          // Set protection selection from trade document
          const userId = auth.currentUser?.uid;
          if (userId && tradeData.protection) {
            const isFromUser = userId === tradeData.fromUserId;
            const userKey = isFromUser ? 'fromUser' : 'toUser';
            const protectionSelected = tradeData.protection[userKey] || false;
            setIncludeProtection(protectionSelected);
            console.log('✅ Loaded protection selection from trade:', protectionSelected);
          }

          // Fetch usernames for both users
          const fromUserRef = doc(db, 'publicProfiles', tradeData.fromUserId);
          const toUserRef = doc(db, 'publicProfiles', tradeData.toUserId);

          const [fromSnap, toSnap] = await Promise.all([
            getDoc(fromUserRef),
            getDoc(toUserRef)
          ]);

          const fromUsername = fromSnap.exists() ? fromSnap.data().username || 'User A' : 'User A';
          const toUsername = toSnap.exists() ? toSnap.data().username || 'User B' : 'User B';

          setUsernames({ fromUser: fromUsername, toUser: toUsername });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching trade:', err);
        setMessage('Error loading trade details');
        // A failed load (no access / network) surfaces the friendly terminal
        // state rather than spinning on "Loading" forever.
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    // Wait for auth to resolve before fetching/deciding so a logged-in user on a
    // fresh load / pull-to-refresh reload sees loading (then the page) rather than
    // a flash of "Please log in" / "Trade not found".
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (user) {
        fetchTrade();
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [id]);

  // Reconcile payout-onboarding status with Stripe, then read the authoritative
  // flag back from Firestore. `checkStripeAccountStatus` only sets
  // stripeOnboardingComplete when the account is truly ready (details submitted +
  // transfers active), so dismissing the sheet early can never mark it ready.
  // Returns the resolved completion boolean.
  const recheckPayoutStatus = async (stripeAccountIdOverride) => {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      let stripeAccountId = snap.exists() ? snap.data().stripeAccountId : null;
      if (stripeAccountIdOverride) stripeAccountId = stripeAccountIdOverride;

      await fetch(`${STRIPE_FN_BASE}/checkStripeAccountStatus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ userId: user.uid, stripeAccountId: stripeAccountId || null })
      });

      const fresh = await getDoc(userRef);
      const complete = fresh.exists() ? !!fresh.data().stripeOnboardingComplete : false;
      setPayoutReady(complete);
      return complete;
    } catch (err) {
      console.error('Error rechecking payout status:', err);
      return false;
    }
  };

  // Baseline read on mount (covers direct visits / prior-session onboarding) so
  // we know whether to gate before the user can tap settle.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) return;
        const complete = snap.exists() ? !!snap.data().stripeOnboardingComplete : false;
        // Race guard: this baseline read and the onboarding-return recheck both run
        // on mount. The recheck hits live Stripe and is authoritative, while this
        // read can resolve late with a pre-webhook stale doc — so never downgrade a
        // confirmed-true here, only fill in when still undetermined/false.
        setPayoutReady(prev => (prev === true ? true : complete));
        setReferralCredits(snap.exists() ? (snap.data().referralCredits || 0) : 0);
      } catch {
        if (!cancelled) setPayoutReady(prev => (prev === true ? true : false));
      }
    })();
    return () => { cancelled = true; };
  }, [auth.currentUser]);

  // On return from onboarding, reconcile against Stripe (handles the native
  // handoff where the account id rides in on the URL).
  useEffect(() => {
    if (searchParams.get('onboarding') !== 'return') return;
    const urlStripeAccountId = new URLSearchParams(window.location.search).get('stripeAccountId');
    recheckPayoutStatus(urlStripeAccountId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, auth.currentUser]);

  // Start (or resume) Stripe Connect payout onboarding for a cash-receiver who
  // hasn't finished it. Mirrors TradeConfirm: HTTPS handoff return_url + isApp so
  // a capacitor:// URL never reaches Stripe; native opens the in-app sheet and
  // re-verifies on return (the sheet's X must NOT mark payouts ready).
  const handleSetupPayout = async () => {
    setSettingUpPayout(true);
    setMessage('');
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Authentication required');
      const idToken = await user.getIdToken();
      const returnUrl = `${window.location.origin}/trade-settlement/${id}?onboarding=return`;

      const resp = await fetch(`${STRIPE_FN_BASE}/createStripeConnectAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({
          userId: user.uid,
          tradeId: id,
          returnUrl,
          returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
        })
      });
      const data = await resp.json();

      if (data.alreadyOnboarded) {
        setPayoutReady(true);
        // Tell the user why Stripe onboarding never appeared (especially the
        // email-reconnect case) instead of silently flipping the gate.
        setPayoutLinkedNotice({ reconnected: !!data.reconnected });
        return;
      }
      if (!data.onboardingUrl) throw new Error('Could not start payout setup. Please try again.');

      const acctId = data.stripeAccountId;
      // Native: in-app sheet; onReturn fires on ANY dismissal, so we re-verify
      // with Stripe and only treat payouts as ready if onboarding truly finished.
      // Web: full-page redirect; Stripe's return_url brings them back here with
      // ?onboarding=return and the effect above reconciles.
      await openCheckout(data.onboardingUrl, {
        onReturn: async () => {
          // POLL, don't check once: Stripe can flip `transfers` to active a few
          // seconds after onboarding finishes; a single immediate check would tell
          // a successfully-onboarded user their setup "wasn't finished".
          let complete = false;
          for (let attempt = 0; attempt < 6 && !complete; attempt++) {
            if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
            complete = await recheckPayoutStatus(acctId);
          }
          if (!complete) {
            setMessage('Payout setup wasn’t finished — tap “Set up payouts” to complete it before settling.');
          }
        }
      });
    } catch (err) {
      console.error('Error starting payout setup:', err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setSettingUpPayout(false);
    }
  };

  // NOTE: settleTrade writes stepTimestamps.{role}_payment server-side, so the
  // old updateStepTimestamp('payment') call is no longer needed here.

  const handleSettleTrade = async () => {
    setProcessing(true);
    setMessage('');

    try {
      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('Authentication required');

      // Gate (defense-in-depth; the UI already swaps the button when setup is
      // needed): a NET cash-receiver must have a completed Stripe payout account
      // before settling. Uses the same shared net value as the render gate.
      if (userNeedsPayoutOnboarding({ netCashReceived, stripeOnboardingComplete: payoutReady === true })) {
        setMessage('Please set up your payout account before settling so you can receive your funds.');
        return; // finally resets `processing`
      }

      // Settle SERVER-SIDE via the settleTrade callable. Besides marking this
      // user paid (what the old client updateDoc did), the server computes the
      // cash receiver's NET payout (fees applied from cash, membership tier,
      // referral credit) and persists it as payoutAdjustment — the amount the
      // escrow release actually transfers. Client-side math here is display
      // only and must never be the source of truth for money movement.
      console.log('🔄 Settling trade server-side:', { tradeId: trade.id, userId });
      const settleTradeFn = httpsCallable(functions, 'settleTrade');
      const settleResult = await settleTradeFn({ tradeId: trade.id });
      console.log('✅ Trade settled:', settleResult.data);

      setMessage('✅ Trade settled successfully! Redirecting to trade progress...');
      
      // Redirect to trade progress after a short delay
      setTimeout(() => {
        navigate(`/trade-progress/${trade.id}`);
      }, 2000);

    } catch (err) {
      console.error('Error settling trade:', err);
      setMessage(`Error settling trade: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Auth still resolving (e.g. right after a pull-to-refresh reload) — show a
  // neutral loading state instead of prematurely deciding login/not-found.
  if (!authChecked) return <div className="p-4">Loading trade...</div>;

  if (!currentUser) {
    sessionStorage.setItem('returnAfterLogin', window.location.pathname);
    return (
      <div className="p-4 text-red-600">
        Please log in to view this trade.{' '}
        <a href="/login" style={{ color: '#2563eb', textDecoration: 'underline' }}>
          Click here to log in
        </a>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading trade...</div>;

  // Only after a completed fetch with a real user do we surface "not found".
  if (notFound) return <div className="p-4 text-gray-600">We couldn&apos;t find this trade. It may have been removed, or you may not have access to it.</div>;

  // Data still settling (transient null between fetches) — keep showing loading.
  if (!trade) return <div className="p-4">Loading trade...</div>;

  const userId = auth.currentUser?.uid;
  const isFromUser = userId === trade.fromUserId;
  const onboardingComplete = searchParams.get('onboarding') === 'complete';

  // Calculate cash flow and fees
  const cash = Number(trade.cashAmount) || 0;
  const isCurrentUserInitiator = auth.currentUser?.uid === trade.fromUserId;
  const myPedalCount = isCurrentUserInitiator ? getInitiatorPedalIds(trade).length : 1;
  let platformFee = getTradeCompletionFee(myPedalCount);
  // Referral credit waives the platform fee — mirror TradeConfirm so a referral
  // user sees the same fee total on both pages (previously settlement showed the
  // full fee even though confirm had waived it).
  const hasReferralCredit = platformFee > 0 && referralCredits > 0;
  if (hasReferralCredit) platformFee = 0;
  const protectionFee = includeProtection ? getTradeProtectFee() : 0; // Dynamic based on membership
  // Cash processing fee is only charged to the person PAYING cash, not receiving it
  const cashFee = 0; // Receiver doesn't pay this fee
  const totalFees = platformFee + protectionFee + cashFee;
  
  // Receiver + net-cash via the SHARED helpers (tradeUtils) — same logic the
  // TradeConfirm button label and onboarding redirect use, so the payout gate
  // can't diverge between the two pages. Settlement always applies received cash
  // to fees (no toggle), so applyCashCredit is true here.
  const userReceivesCash = getUserReceivesCash(trade, userId);

  // Calculate how much of the received cash is applied to fees
  const appliedToFees = Math.min(cash, totalFees);
  const remainingCash = cash - appliedToFees;

  // NET payout this user actually pockets. Gate on NET (not gross receiver) so a
  // receiver whose cash is fully eaten by fees isn't forced to set up payouts.
  const netCashReceived = getNetCashReceived({
    grossReceived: userReceivesCash ? cash : 0,
    totalFees,
    applyCashCredit: true
  });
  const requiresPayout = netCashReceived > 0;

  // Payout gating. payoutReady: null = still checking.
  const needsPayoutSetup = requiresPayout && payoutReady === false;
  const payoutChecking = requiresPayout && payoutReady === null;

  return (
    <div className="trade-settlement-outer" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '1.5rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <style>{`
        @media (max-width: 768px) {
          .trade-settlement-outer {
            padding: 0.75rem !important;
          }
          .trade-settlement-card {
            padding: 1rem !important;
            border-radius: 8px !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            max-width: 100% !important;
          }
          .trade-settlement-outer h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
            padding: 0 0.25rem !important;
          }
          .trade-settlement-footer {
            padding-bottom: 80px !important;
          }
        }
      `}</style>
      <h2 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
        {onboardingComplete ? '✅ Payout Account Ready!' : 'Settle Trade'}
      </h2>

      {onboardingComplete && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
          <p className="text-green-800 mb-2">
            🎉 Great! Your payout account is now set up and ready to receive payments.
          </p>
          <p className="text-green-700 text-sm">
            Since you're receiving ${cash.toFixed(2)} in cash and applying it to your fees, 
            no additional payment is required.
          </p>
        </div>
      )}

      {/* Payout Information - Only show when receiving cash */}
      {cash > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg" style={{ maxWidth: '700px', margin: '0 auto 16px auto', padding: '16px 20px', width: '100%', boxSizing: 'border-box' }}>
          <p className="text-blue-800 text-sm" style={{ margin: 0, lineHeight: '1.5' }}>
            <strong>Automatic Payout:</strong> Once this trade is complete and funds are released, your balance will automatically transfer to your connected bank account within 2 business days.
          </p>
        </div>
      )}

      <div className="trade-settlement-card" style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', fontFamily: 'Nunito, sans-serif', maxWidth: '700px', margin: '0 auto 24px auto', width: '100%', boxSizing: 'border-box' }}>
        <h4 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>Payment breakdown</h4>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Trading with</span>
          <span style={{ fontWeight: '600', color: '#374151' }}>
            {isFromUser ? usernames.toUser || 'another user' : usernames.fromUser || 'another user'}
          </span>
        </div>

        {cash > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Cash you're receiving</span>
            <span style={{ fontWeight: '600', color: '#15803d' }}>${cash.toFixed(2)}</span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
          <span style={{ color: '#374151' }}>Total fees</span>
          <span style={{ fontWeight: '600', color: '#374151' }}>${totalFees.toFixed(2)}</span>
        </div>

        {totalFees > 0 && includeProtection && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.8rem', paddingLeft: '0.75rem' }}>
            <span style={{ color: '#6b7280' }}>Includes TradeProtect</span>
            <span style={{ fontWeight: '600', color: '#6b7280' }}>${protectionFee.toFixed(2)}</span>
          </div>
        )}

        {appliedToFees > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Applied to fees</span>
            <span style={{ fontWeight: '600', color: '#15803d' }}>-${appliedToFees.toFixed(2)}</span>
          </div>
        )}

        <div style={{ borderTop: '2px solid #d1d5db', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>You'll receive</span>
          <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#15803d' }}>
            ${remainingCash.toFixed(2)}
          </span>
        </div>

        {cash > 0 && (
          <div style={{ background: '#e8f5f0', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '0.75rem', marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.85rem', color: '#2e7d6e' }}>
              Nothing to pay — you'll receive ${remainingCash.toFixed(2)} once the trade is complete.
            </span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
          {needsPayoutSetup ? (
            <div style={{ width: '100%', maxWidth: '360px' }}>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', textAlign: 'left' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                  <strong>You're receiving ${cash.toFixed(2)} from this trade.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                </p>
              </div>
              <button
                onClick={handleSetupPayout}
                disabled={settingUpPayout}
                style={{
                  backgroundColor: settingUpPayout ? '#9CA3AF' : '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: settingUpPayout ? 'not-allowed' : 'pointer',
                  width: '100%',
                  opacity: settingUpPayout ? 0.7 : 1
                }}
              >
                {settingUpPayout ? 'Opening secure setup…' : `Set up payout to receive $${cash.toFixed(2)}`}
              </button>
            </div>
          ) : payoutChecking ? (
            <button
              disabled
              style={{
                backgroundColor: '#9CA3AF', color: 'white', border: 'none', borderRadius: '9999px',
                padding: '12px 24px', fontFamily: 'Nunito, sans-serif', fontWeight: 'bold', fontSize: '16px',
                cursor: 'not-allowed', maxWidth: '300px', width: '100%', opacity: 0.7
              }}
            >
              Checking payout setup…
            </button>
          ) : (
            <button
              onClick={handleSettleTrade}
              disabled={processing}
              style={{
                backgroundColor: processing ? '#9CA3AF' : '#C9A227',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '12px 24px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: processing ? 'not-allowed' : 'pointer',
                maxWidth: '300px',
                width: '100%',
                opacity: processing ? 0.7 : 1
              }}
            >
              {processing ? 'Processing...' : 'Settle Trade & Continue'}
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`mt-4 p-3 rounded ${
          message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="py-4 trade-settlement-footer" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      <PayoutAccountLinkedModal
        isOpen={!!payoutLinkedNotice}
        reconnected={payoutLinkedNotice?.reconnected}
        onClose={() => setPayoutLinkedNotice(null)}
      />
    </div>
  );
};

export default TradeSettlement; 